import { FoodCategoryPortion } from '@/types/food-category';

type PortionTemplate = {
  servingId: string;
  name: string;
  grams: number;
  isDefault?: boolean;
};

const GENERIC_PORTION_TEMPLATES: PortionTemplate[] = [
  { servingId: 'serving', name: 'Serving', grams: 150, isDefault: true },
  { servingId: 'piece', name: 'Piece', grams: 100 },
  { servingId: 'cup', name: 'Cup', grams: 240 },
];

const DISH_TYPE_PORTION_TEMPLATES: Record<string, PortionTemplate[]> = {
  'main-dish': [
    { servingId: 'plate', name: 'Plate', grams: 350, isDefault: true },
    { servingId: 'bowl', name: 'Bowl', grams: 300 },
    { servingId: 'serving', name: 'Serving', grams: 250 },
  ],
  'side-dish': [
    { servingId: 'small_bowl', name: 'Small bowl', grams: 150, isDefault: true },
    { servingId: 'small_plate', name: 'Small plate', grams: 180 },
    { servingId: 'serving', name: 'Serving', grams: 120 },
  ],
  appetizer: [
    { servingId: 'piece', name: 'Piece', grams: 50, isDefault: true },
    { servingId: 'small_plate', name: 'Small plate', grams: 120 },
  ],
  dessert: [
    { servingId: 'slice', name: 'Slice', grams: 100, isDefault: true },
    { servingId: 'piece', name: 'Piece', grams: 80 },
    { servingId: 'scoop', name: 'Scoop', grams: 60 },
  ],
  beverage: [
    { servingId: 'glass', name: 'Glass', grams: 240, isDefault: true },
    { servingId: 'mug', name: 'Mug', grams: 300 },
    { servingId: 'bottle', name: 'Bottle', grams: 500 },
  ],
  bread: [
    { servingId: 'slice', name: 'Slice', grams: 35, isDefault: true },
    { servingId: 'piece', name: 'Piece', grams: 70 },
  ],
  snack: [
    { servingId: 'piece', name: 'Piece', grams: 60, isDefault: true },
    { servingId: 'handful', name: 'Handful', grams: 30 },
    { servingId: 'small_bowl', name: 'Small bowl', grams: 80 },
  ],
  soup: [
    { servingId: 'bowl', name: 'Bowl', grams: 350, isDefault: true },
    { servingId: 'cup', name: 'Cup', grams: 240 },
    { servingId: 'mug', name: 'Mug', grams: 300 },
  ],
  sauce: [
    { servingId: 'tbsp', name: 'Tablespoon', grams: 15, isDefault: true },
    { servingId: 'tsp', name: 'Teaspoon', grams: 5 },
    { servingId: 'serving', name: 'Serving', grams: 30 },
  ],
  salad: [
    { servingId: 'bowl', name: 'Bowl', grams: 180, isDefault: true },
    { servingId: 'plate', name: 'Plate', grams: 160 },
  ],
  sandwich: [
    { servingId: 'half_sandwich', name: 'Half sandwich', grams: 110, isDefault: true },
    { servingId: 'sandwich', name: 'Sandwich', grams: 220 },
  ],
  pasta: [
    { servingId: 'bowl', name: 'Bowl', grams: 260, isDefault: true },
    { servingId: 'plate', name: 'Plate', grams: 300 },
    { servingId: 'serving', name: 'Serving', grams: 220 },
  ],
  pizza: [
    { servingId: 'slice', name: 'Slice', grams: 120, isDefault: true },
    { servingId: 'pizza', name: 'Whole pizza', grams: 720 },
  ],
  'stir-fry': [
    { servingId: 'bowl', name: 'Bowl', grams: 280, isDefault: true },
    { servingId: 'plate', name: 'Plate', grams: 320 },
  ],
  casserole: [
    { servingId: 'square', name: 'Square', grams: 180, isDefault: true },
    { servingId: 'serving', name: 'Serving', grams: 250 },
  ],
  roast: [
    { servingId: 'slice', name: 'Slice', grams: 90, isDefault: true },
    { servingId: 'plate', name: 'Plate', grams: 300 },
    { servingId: 'serving', name: 'Serving', grams: 220 },
  ],
};

const toPortionOption = (template: PortionTemplate, sortOrder: number): FoodCategoryPortion => ({
  servingId: template.servingId,
  id: template.servingId,
  name: template.name,
  gramsOverride: template.grams,
  weightG: template.grams,
  suggestedWeightG: template.grams,
  isDefault: template.isDefault,
  sortOrder,
});

const KNOWN_TEMPLATE_PORTION_IDS = new Set<string>(
  [
    ...GENERIC_PORTION_TEMPLATES,
    ...Object.values(DISH_TYPE_PORTION_TEMPLATES).flat(),
  ].map((template) => normalizeRecipePortionId(template.servingId))
);

export function normalizeRecipePortionId(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function isSuggestedRecipePortionId(value: unknown): boolean {
  return KNOWN_TEMPLATE_PORTION_IDS.has(normalizeRecipePortionId(value));
}

export function portionOptionValue(portion: FoodCategoryPortion, index: number): string {
  const raw = portion.servingId ?? portion.id ?? portion.name ?? `portion-${index}`;
  return normalizeRecipePortionId(raw);
}

export function slugifyPortionName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function getSuggestedPortionsForDishTypes(dishTypes: string[]): FoodCategoryPortion[] {
  const normalizedDishTypes = Array.from(new Set(
    dishTypes
      .map((dishType) => normalizeRecipePortionId(dishType))
      .filter(Boolean)
  ));

  const templates = normalizedDishTypes.flatMap((dishType) => DISH_TYPE_PORTION_TEMPLATES[dishType] ?? []);
  const sourceTemplates = templates.length > 0 ? templates : GENERIC_PORTION_TEMPLATES;

  const seen = new Set<string>();
  const portions: FoodCategoryPortion[] = [];
  sourceTemplates.forEach((template, index) => {
    if (seen.has(template.servingId)) return;
    seen.add(template.servingId);
    portions.push(toPortionOption(template, index));
  });

  return portions;
}

export function mergePortionOptions(
  suggestions: FoodCategoryPortion[],
  existing: FoodCategoryPortion[],
): FoodCategoryPortion[] {
  const existingMap = new Map<string, FoodCategoryPortion>();
  existing.forEach((portion, index) => {
    const key = portionOptionValue(portion, index);
    if (!key || existingMap.has(key)) return;
    existingMap.set(key, portion);
  });

  const merged: FoodCategoryPortion[] = [];
  const seen = new Set<string>();

  suggestions.forEach((portion, index) => {
    const key = portionOptionValue(portion, index);
    if (!key || seen.has(key)) return;
    const persisted = existingMap.get(key);
    merged.push({
      ...portion,
      ...persisted,
      servingId: key,
      id: key,
      name: persisted?.name || portion.name || key,
      isCustom: persisted?.isCustom === true,
      gramsOverride: persisted?.gramsOverride ?? persisted?.weightG ?? portion.gramsOverride ?? portion.weightG,
      weightG: persisted?.weightG ?? persisted?.gramsOverride ?? portion.weightG ?? portion.gramsOverride,
      suggestedWeightG: persisted?.suggestedWeightG ?? portion.suggestedWeightG,
      isDefault: persisted?.isDefault ?? portion.isDefault,
    });
    seen.add(key);
  });

  existing.forEach((portion, index) => {
    const key = portionOptionValue(portion, index);
    if (!key || seen.has(key)) return;
    merged.push({
      ...portion,
      servingId: key,
      id: key,
      name: portion.name || key,
    });
    seen.add(key);
  });

  return merged;
}
